import { useEffect, useState, type ReactNode } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ChevronDownRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  EyeOffRegular,
  EyeRegular,
  LockClosedRegular,
  SettingsRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useBranding } from '../theme/BrandingProvider';
import { useSession } from '../auth/SessionProvider';
import { usePrivacy } from './PrivacyProvider';
import { ThemeSwitcher } from './ThemeSwitcher';
import { ActiveUsersBubbles } from './ActiveUsersBubbles';
import { NotificationBell } from './NotificationBell';
import { usePresencePing } from '../api/usePresence';
import { useApi } from '../api/useApi';
import { featureNav } from '../config/featureNav';
import './AppShell.css';

const RAIL_KEY = 'm365adminbox.nav.rail';
const COLLAPSED_KEY = 'm365adminbox.nav.collapsed';

function loadCollapsed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const branding = useBranding();
  const { me } = useSession();
  const { confidential, toggle } = usePrivacy();
  usePresencePing();
  const api = useApi();

  const [photoUrl, setPhotoUrl] = useState<string>();
  useEffect(() => {
    let url: string | null = null;
    api.getMyPhotoUrl().then((u) => {
      url = u;
      if (u) setPhotoUrl(u);
    });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [api]);

  const [rail, setRail] = useState(() => localStorage.getItem(RAIL_KEY) === '1');
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const name = me?.displayName ?? account?.name ?? account?.username ?? 'Konto';
  const deniedSet = new Set(me?.deniedFunctions ?? []);
  const restricted = me?.roleRestricted ?? false;
  const allowedSet = new Set(me?.allowedFunctions ?? []);

  function toggleRail() {
    setRail((r) => {
      const next = !r;
      localStorage.setItem(RAIL_KEY, next ? '1' : '0');
      return next;
    });
  }

  function toggleCategory(label: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      return next;
    });
  }

  // Rollen-eingegrenzte Nutzer (z. B. Kunde): nur erlaubte Funktionen — Items werden
  // ausgeblendet, nicht gesperrt; die adminOnly-Sektionsgrenze entfällt (Allow-Liste regelt).
  const sections = featureNav
    .map((s) => ({
      ...s,
      items: restricted ? s.items.filter((i) => i.pageKey && allowedSet.has(i.pageKey)) : s.items,
    }))
    .filter((s) => (restricted ? s.items.length > 0 : !s.adminOnly || me?.isAdmin));

  return (
    <div className="shell-root">
      <header className="shell-header">
        <div className="shell-brand">
          {branding.logoUrl ? <img src={branding.logoUrl} alt="" /> : null}
          <span className="shell-brand-name">{branding.appName}</span>
        </div>

        <div className="shell-header-actions">
          <Tooltip
            relationship="label"
            content={confidential ? 'Vertraulichkeitsmodus: an (PII unkenntlich)' : 'Vertraulichkeitsmodus: aus'}
          >
            <Button
              appearance="transparent"
              aria-pressed={confidential}
              style={{ color: 'var(--colorNeutralForegroundOnBrand)' }}
              icon={confidential ? <EyeOffRegular /> : <EyeRegular />}
              onClick={toggle}
            />
          </Tooltip>

          {me?.isAdmin ? (
            <Tooltip relationship="label" content="Einstellungen">
              <Button
                appearance="transparent"
                aria-label="Einstellungen"
                style={{ color: 'var(--colorNeutralForegroundOnBrand)' }}
                icon={<SettingsRegular />}
                onClick={() => navigate('/settings')}
              />
            </Tooltip>
          ) : null}

          <ActiveUsersBubbles />

          <ThemeSwitcher />

          <NotificationBell />

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="transparent"
                shape="circular"
                aria-label="Konto"
                icon={<Avatar name={name} image={photoUrl ? { src: photoUrl } : undefined} size={28} color="colorful" />}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem disabled>
                  <span className="pii">{name}</span>
                </MenuItem>
                <MenuItem icon={<SignOutRegular />} onClick={() => instance.logoutRedirect()}>
                  Abmelden
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      <div className="shell-body">
        <nav className={mergeClasses('shell-nav', rail && 'rail')} aria-label="Hauptnavigation">
          <div className="shell-nav-items">
            {sections.map((section) => {
              const isCollapsed = collapsed.has(section.label);
              return (
                <div key={section.label} className="shell-section">
                  {!rail ? (
                    <button
                      className="shell-section-header"
                      onClick={() => toggleCategory(section.label)}
                      aria-expanded={!isCollapsed}
                    >
                      {isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
                      <span className="shell-section-label">{section.label}</span>
                      {isCollapsed ? <span className="shell-section-count">({section.items.length})</span> : null}
                    </button>
                  ) : null}

                  {rail || !isCollapsed
                    ? section.items.map((item) => {
                        const selected = location.pathname === item.key;
                        const denied = item.pageKey ? deniedSet.has(item.pageKey) : false;
                        const button = (
                          <Button
                            key={item.key}
                            appearance="subtle"
                            icon={rail && denied ? <LockClosedRegular className="shell-nav-lock-rail" /> : item.icon}
                            disabled={denied}
                            className={mergeClasses('shell-nav-btn', selected && 'selected', rail && 'rail')}
                            onClick={() => navigate(item.key)}
                          >
                            {rail ? null : (
                              <>
                                {item.text}
                                {denied ? (
                                  <LockClosedRegular className="shell-nav-lock" />
                                ) : item.soon ? (
                                  <Badge className="shell-nav-badge" size="small" appearance="tint" color="informative">
                                    soon
                                  </Badge>
                                ) : null}
                              </>
                            )}
                          </Button>
                        );
                        const tooltip = denied ? `${item.text} — keine Berechtigung` : item.text;
                        return rail ? (
                          <Tooltip key={item.key} relationship="label" content={tooltip} positioning="after">
                            {button}
                          </Tooltip>
                        ) : (
                          button
                        );
                      })
                    : null}
                </div>
              );
            })}
          </div>

          <div className="shell-nav-footer">
            <Tooltip relationship="label" content={rail ? 'Navigation ausklappen' : 'Navigation einklappen'} positioning="after">
              <Button
                appearance="subtle"
                className={mergeClasses('shell-nav-btn', rail && 'rail')}
                icon={rail ? <ChevronRightRegular /> : <ChevronLeftRegular />}
                onClick={toggleRail}
              >
                {rail ? null : 'Einklappen'}
              </Button>
            </Tooltip>
          </div>
        </nav>
        <main className="shell-content">{children}</main>
      </div>
    </div>
  );
}
